import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { clearCanICache } from './useCanI';
import { isCancel, passkeySignIn, passkeySupported } from './passkey';

/**
 * AuthContext — operator-identity + session lifecycle.
 *
 * Pattern matches what the BFF exposes:
 *   POST /auth/login   {email, password} → operator row + Set-Cookie
 *   POST /auth/logout                    → 204 + cleared cookie
 *   GET  /auth/me                        → operator row | 401
 *
 * Cookies (HttpOnly aveq_admin_session) are automatic; the browser
 * sends them on same-origin requests. This context just tracks the
 * resolved operator state.
 *
 * States:
 *   { operator: null, loading: true,  error: null }  — initial probe in flight
 *   { operator: row,  loading: false, error: null }  — logged in
 *   { operator: null, loading: false, error: null }  — not logged in
 *   { operator: null, loading: false, error: msg  }  — last login attempt failed
 *
 * Consumers should usually wrap with <LoginGate> rather than reading
 * the context directly — LoginGate renders the form when there's no
 * operator and the children when there is.
 */

const AuthCtx = createContext(null);


export function AuthProvider({ children }) {
  const [operator, setOperator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // On mount: probe /auth/me. 200 → set operator; 401 → leave null.
  // Anything else: surface as `error` but keep loading=false so the
  // login form renders rather than spinning forever.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch('/auth/me', { credentials: 'same-origin' });
        if (cancelled) return;
        if (r.ok) {
          const op = await r.json();
          setOperator(op);
        } else if (r.status === 401) {
          setOperator(null);
        } else {
          setError(`unexpected /auth/me status: ${r.status}`);
        }
      } catch (e) {
        if (!cancelled) setError(String(e.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Global 401 interceptor — bounces stale-session users back to the
  // login screen instead of letting the UI silently retry every
  // authenticated call forever (the symptom that surfaced 2026-05-27:
  // expired cookie → /api/topology, /api/probe, /api/networks/ all
  // 401 on a poll loop without ever prompting re-auth). Wraps
  // window.fetch on AuthProvider mount; restores the original on
  // unmount. Reacts only to /api/* responses — leaves /auth/login,
  // /auth/me, and external-origin requests untouched (the components
  // that call those want to handle their own 401 semantics).
  useEffect(() => {
    const originalFetch = window.fetch.bind(window);
    window.fetch = async (...args) => {
      const resp = await originalFetch(...args);
      try {
        const reqUrl = typeof args[0] === 'string' ? args[0] : args[0]?.url;
        if (resp.status === 401 && reqUrl && reqUrl.startsWith('/api/')) {
          // Stale session — drop client-side state so LoginGate
          // re-renders and shows the login form. Don't POST
          // /auth/logout (the cookie is already invalid; the server
          // would 401 us back anyway).
          clearCanICache();
          setOperator(null);
        }
      } catch (_) {
        // Best-effort introspection — never let interceptor logic
        // mask the underlying response from the caller.
      }
      return resp;
    };
    return () => { window.fetch = originalFetch; };
  }, []);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const r = await fetch('/auth/login', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (!r.ok) {
        setError(await loginError(r));
        return false;
      }
      const op = await r.json();
      // Operator identity is changing — drop the useCanI cache so the
      // new session's permission checks fetch fresh. Without this,
      // signing in as a less-privileged operator after a more-
      // privileged one would inherit the cached can:true values and
      // wrongly show controls the new operator can't actually use.
      clearCanICache();
      setOperator(op);
      return true;
    } catch (e) {
      setError(String(e.message || e));
      return false;
    }
  }, []);

  // admin-passkey-login: the chat account's passkey; aveq-agent checks it.
  const loginWithPasskey = useCallback(async () => {
    setError(null);
    try {
      const r = await passkeySignIn();
      if (!r) return false;
      if (!r.ok) {
        setError(await loginError(r));
        return false;
      }
      clearCanICache();
      setOperator(await r.json());
      return true;
    } catch (e) {
      if (!isCancel(e)) setError(String(e.message || e));
      return false;
    }
  }, []);

  const linkAccount = useCallback(async (email, password, operatorPassword) => {
    const r = await fetch('/auth/link-account', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, operator_password: operatorPassword }),
    });
    if (!r.ok) return loginError(r);
    setOperator(await r.json());
    return null;
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch('/auth/logout', { method: 'POST', credentials: 'same-origin' });
    } catch (_) {
      // Best-effort — server-side row is gone via cookie revocation; ignore.
    }
    clearCanICache();
    setOperator(null);
  }, []);

  return (
    <AuthCtx.Provider value={{ operator, loading, error, login, loginWithPasskey, logout, linkAccount }}>
      {children}
    </AuthCtx.Provider>
  );
}


// The server's reason ("403: not an operator") as a sentence for the form.
export async function loginError(r) {
  if (r.status === 401) return 'Invalid email or password.';
  if (r.status === 429) return 'Too many attempts. Wait a minute and try again.';
  const reason = (await r.text().catch(() => '')).replace(/^\d{3}:\s*/, '').trim();
  if (reason === 'admin password is incorrect') return 'Your admin password is incorrect.';
  if (reason === 'account suspended') return 'This account is suspended.';
  return reason || `Sign-in failed (HTTP ${r.status}).`;
}


export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}


/**
 * Wraps children — renders the login form (or a loading state) when
 * no operator is signed in, the children when one is.
 */
export function LoginGate({ children }) {
  const { operator, loading } = useAuth();
  if (loading) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100vh', fontFamily: 'system-ui, sans-serif', color: '#888',
      }}>
        Checking session…
      </div>
    );
  }
  if (!operator) {
    return <LoginForm />;
  }
  return (
    <>
      {operator.linked === false && <LinkAccountBanner />}
      {children}
    </>
  );
}


// Shown to an operator still on their old admin password: link the chat account
// they'll sign in with from now on.
export function LinkAccountBanner() {
  const { linkAccount } = useAuth();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(await linkAccount(email.trim(), password, adminPassword));
    setBusy(false);
  };

  return (
    <div data-testid="link-account-banner" style={{
      background: '#fff8e1', borderBottom: '1px solid #f0d58c', padding: '8px 16px',
      fontFamily: 'system-ui, sans-serif', fontSize: '0.85rem',
    }}>
      {!open ? (
        <>
          You're signed in with your old admin password. Link your aveq chat account to sign
          in with it instead; the old password stops working once linked.{' '}
          <button onClick={() => setOpen(true)} style={bannerBtn}>Link chat account</button>
        </>
      ) : (
        <form onSubmit={onSubmit} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span>Chat account:</span>
          <input aria-label="chat email" placeholder="email" value={email}
                 onChange={(e) => setEmail(e.target.value)} required autoComplete="username" />
          <input aria-label="chat password" placeholder="password" type="password" value={password}
                 onChange={(e) => setPassword(e.target.value)} required autoComplete="off" />
          <span>Current admin password:</span>
          <input aria-label="admin password" type="password" value={adminPassword}
                 onChange={(e) => setAdminPassword(e.target.value)} required autoComplete="current-password" />
          <button type="submit" disabled={busy} style={bannerBtn}>{busy ? 'Linking…' : 'Link'}</button>
          <button type="button" onClick={() => setOpen(false)} style={bannerBtn}>Cancel</button>
          {error && <span style={{ color: '#c00' }}>{error}</span>}
        </form>
      )}
    </div>
  );
}

const bannerBtn = {
  padding: '3px 10px', border: '1px solid #c9a94a', background: '#fff',
  borderRadius: 4, cursor: 'pointer', fontSize: '0.8rem',
};


function LoginForm() {
  const { login, loginWithPasskey, error } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Shown only when aveq-agent's passkey switch is on and this browser can do it.
  const [passkeys, setPasskeys] = useState(false);
  // admin-passkey-only: the server refuses passwords, so the form offers the passkey alone.
  const [passkeyOnly, setPasskeyOnly] = useState(false);
  useEffect(() => {
    let live = true;
    fetch('/auth/passkey', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!live || !d) return;
        setPasskeys(d.enabled === true && passkeySupported());
        setPasskeyOnly(d.passkey_only === true);
      })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  const onPasskey = async () => {
    setSubmitting(true);
    await loginWithPasskey();
    setSubmitting(false);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setSubmitting(true);
    await login(email.trim(), password);
    // If login succeeded, AuthProvider state flips and LoginGate
    // unmounts us; otherwise stay so the error message shows.
    setSubmitting(false);
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100vh', background: '#fafafa', fontFamily: 'system-ui, sans-serif',
    }}>
      <form onSubmit={onSubmit} style={{
        background: '#fff', padding: '32px', borderRadius: '8px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)', width: '320px',
        display: 'flex', flexDirection: 'column', gap: '12px',
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8 }}>
          <img src="/aveqlogo.svg" alt="" style={{ height: '32px' }} />
          <strong style={{ fontSize: '1.1rem' }}>aveq-admin</strong>
        </div>
        {passkeyOnly ? (
          <div style={{ color: '#555', fontSize: '0.85rem', lineHeight: 1.45 }}>
            Admin sign-in uses a passkey. No passkey yet? Add one on your aveq.io profile
            (Profile → Passkeys), then come back here.
            {!passkeys && (
              <div style={{ color: '#c00', marginTop: 8 }}>This browser can’t use passkeys.</div>
            )}
          </div>
        ) : (
          <>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: '0.85rem' }}>Username or email</span>
            <input value={email} onChange={(e) => setEmail(e.target.value)}
                    type="text" autoFocus required autoComplete="username"
                    style={{
                      padding: '8px 10px', border: '1px solid #ccc',
                      borderRadius: '4px', fontSize: '0.9rem',
                    }} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: '0.85rem' }}>Password</span>
            <input value={password} onChange={(e) => setPassword(e.target.value)}
                    type="password" required
                    style={{
                      padding: '8px 10px', border: '1px solid #ccc',
                      borderRadius: '4px', fontSize: '0.9rem',
                    }} />
          </label>
          <div style={{ color: '#888', fontSize: '0.78rem' }}>
            Use your aveq chat account. Not linked yet? Use your old admin login.
          </div>
          </>
        )}
        {error && (
          <div style={{ color: '#c00', fontSize: '0.85rem' }}>{error}</div>
        )}
        {!passkeyOnly && <button type="submit" disabled={submitting} style={{
          padding: '8px 12px', background: '#222', color: '#fff', border: 'none',
          borderRadius: '4px', cursor: 'pointer', fontSize: '0.9rem',
          marginTop: 4,
        }}>
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>}
        {passkeys && (
          <button type="button" onClick={onPasskey} disabled={submitting} style={{
            padding: '8px 12px', background: '#fff', color: '#222', border: '1px solid #ccc',
            borderRadius: '4px', cursor: 'pointer', fontSize: '0.9rem',
          }}>
            Sign in with a passkey
          </button>
        )}
      </form>
    </div>
  );
}
